import React, { useState, useEffect } from 'react';
import ChartCard from './ChartCard';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart, Rectangle } from 'recharts';
import { useAppDispatch, useAppSelector } from '../../store/hooks/reduxHooks';
import { fetchOrderStatusStats } from '../../features/dashboard/dashboardSlice';

// Define status colors for consistent color mapping
const STATUS_COLORS: Record<string, string> = {
  "Completed": "#16a34a", // green-600
  "Verification Pending": "#4f46e5", // indigo-600
  "QR Activated": "#f59e0b", // amber-500
  "Expired": "#ef4444", // red-500
  "Failed": "#dc2626", // red-600
  "Approved": "#0ea5e9", // sky-500
  "In Process": "#8b5cf6", // violet-500
  "Product Verified": "#10b981", // emerald-500
};

// The statuses we want to display with prominence
const PRIMARY_STATUSES = ["Completed", "Verification Pending"];

// All other status names
const SECONDARY_STATUSES = [
  "QR Activated",
  "Expired",
  "Failed",
  "Approved",
  "In Process",
  "Product Verified"
];

// Custom tooltip component
const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    // Filter out undefined entries
    const validPayload = payload.filter((entry: any) => entry && entry.name);

    return (
      <div className="bg-white p-3 border border-gray-200 shadow-lg rounded-md">
        <p className="font-gilroy-semibold text-gray-700 mb-2">{label}</p>
        <div className="space-y-1.5">
          {validPayload.map((entry: any, index: number) => (
            <div key={index} className="flex items-center justify-between">
              <div className="flex items-center">
                <div
                  className="w-3 h-3 rounded-full mr-2"
                  style={{ backgroundColor: STATUS_COLORS[entry.name] || "#9CA3AF" }}
                ></div>
                <span className="text-sm capitalize">{entry.name}</span>
              </div>
              <span className="font-gilroy-semibold text-sm ml-4">{entry.value}</span>
            </div>
          ))}
          <div className="pt-1.5 mt-1.5 border-t border-gray-100">
            <div className="flex items-center justify-between">
              <span className="text-sm font-gilroy-semibold">Total</span>
              <span className="font-gilroy-semibold text-sm">
                {validPayload.reduce((sum: number, entry: any) => sum + (entry.value || 0), 0)}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }
  return null;
};

const OrdersChart: React.FC = () => {
  const dispatch = useAppDispatch();
  const { orderStatusStats: stats, loadingOrderStats: isLoading, hasFetchedOrderStats } = useAppSelector(state => state.dashboard);

  const [timeFrame, setTimeFrame] = useState<'today' | 'thisMonth' | 'lastSixMonths'>('thisMonth');

  useEffect(() => {
    if (!hasFetchedOrderStats && !isLoading) {
      dispatch(fetchOrderStatusStats());
    }
  }, [dispatch, hasFetchedOrderStats, isLoading]);

  // Process the API data into a format suitable for the chart
  const processChartData = () => {
    if (!stats) return { primary: [], secondary: [] };

    if (timeFrame === 'today') {
      // For today's data
      if (!stats.today) {
        return {
          primary: [{
            period: 'Today',
            ...PRIMARY_STATUSES.reduce((acc, status) => {
              acc[status] = 0;
              return acc;
            }, {} as Record<string, number>),
          }],
          secondary: [{
            period: 'Today',
            ...SECONDARY_STATUSES.reduce((acc, status) => {
              acc[status] = 0;
              return acc;
            }, {} as Record<string, number>)
          }]
        };
      }

      const todayStats = stats.today;

      // Create data objects for primary and secondary statuses
      const primaryData: Record<string, any> = {
        period: 'Today',
      };

      const secondaryData: Record<string, any> = {
        period: 'Today',
      };

      // Set values for primary statuses
      PRIMARY_STATUSES.forEach(status => {
        primaryData[status] = todayStats[status] || 0;
      });

      // Set values for secondary statuses
      SECONDARY_STATUSES.forEach(status => {
        secondaryData[status] = todayStats[status] || 0;
      });

      // Add any other statuses not explicitly defined in our constants
      Object.entries(todayStats).forEach(([status, count]) => {
        if (!PRIMARY_STATUSES.includes(status) && !SECONDARY_STATUSES.includes(status)) {
          secondaryData[status] = count;
        }
      });

      return {
        primary: [primaryData],
        secondary: [secondaryData]
      };
    } else if (timeFrame === 'thisMonth') {
      // For monthly data
      if (!stats.thisMonth || !stats.thisMonth.stats) {
        return {
          primary: [{
            period: 'This Month',
            ...PRIMARY_STATUSES.reduce((acc, status) => {
              acc[status] = 0;
              return acc;
            }, {} as Record<string, number>),
          }],
          secondary: [{
            period: 'This Month',
            ...SECONDARY_STATUSES.reduce((acc, status) => {
              acc[status] = 0;
              return acc;
            }, {} as Record<string, number>)
          }]
        };
      }

      const monthStats = stats.thisMonth.stats;

      // Create data objects for primary and secondary statuses
      const primaryData: Record<string, any> = {
        period: 'This Month',
      };

      const secondaryData: Record<string, any> = {
        period: 'This Month',
      };

      // Set values for primary statuses
      PRIMARY_STATUSES.forEach(status => {
        primaryData[status] = monthStats[status] || 0;
      });

      // Set values for secondary statuses
      SECONDARY_STATUSES.forEach(status => {
        secondaryData[status] = monthStats[status] || 0;
      });

      // Add any other statuses not explicitly defined in our constants
      Object.entries(monthStats).forEach(([status, count]) => {
        if (!PRIMARY_STATUSES.includes(status) && !SECONDARY_STATUSES.includes(status)) {
          secondaryData[status] = count;
        }
      });

      return {
        primary: [primaryData],
        secondary: [secondaryData]
      };
    } else {
      // For six months data
      if (!stats.lastSixMonths) {
        return {
          primary: [{
            period: 'Last 6 Months',
            ...PRIMARY_STATUSES.reduce((acc, status) => {
              acc[status] = 0;
              return acc;
            }, {} as Record<string, number>),
          }],
          secondary: [{
            period: 'Last 6 Months',
            ...SECONDARY_STATUSES.reduce((acc, status) => {
              acc[status] = 0;
              return acc;
            }, {} as Record<string, number>)
          }]
        };
      }

      const sixMonthStats = stats.lastSixMonths;

      // Create data objects for primary and secondary statuses
      const primaryData: Record<string, any> = {
        period: 'Last 6 Months',
      };

      const secondaryData: Record<string, any> = {
        period: 'Last 6 Months',
      };

      // Set values for primary statuses
      PRIMARY_STATUSES.forEach(status => {
        primaryData[status] = (sixMonthStats[status] as number) || 0;
      });

      // Set values for secondary statuses
      SECONDARY_STATUSES.forEach(status => {
        secondaryData[status] = (sixMonthStats[status] as number) || 0;
      });

      // Add any other statuses not explicitly defined in our constants
      Object.entries(sixMonthStats).forEach(([status, count]) => {
        if (!PRIMARY_STATUSES.includes(status) && !SECONDARY_STATUSES.includes(status) &&
          status !== 'start' && status !== 'end') {
          const countValue = count as number;
          if (!isNaN(countValue)) {
            secondaryData[status] = countValue;
          }
        }
      });

      return {
        primary: [primaryData],
        secondary: [secondaryData]
      };
    }
  };

  const { primary: primaryChartData, secondary: secondaryChartData } = !isLoading && stats ? processChartData() : { primary: [], secondary: [] };

  // Custom gradient definitions for bars
  const renderGradients = () => {
    return Object.entries(STATUS_COLORS).map(([status, baseColor]) => {
      // Create a slightly darker shade for the gradient end
      let darkerColor = baseColor;

      // Derive darker shade (simplified)
      const darkerShade = baseColor.replace(/\d+(?=\)|\s|$)/, (match) =>
        Math.max(0, parseInt(match) - 100).toString()
      );

      darkerColor = darkerShade;

      const gradientId = `${status.replace(/\s+/g, '')}Gradient`;

      return (
        <linearGradient key={gradientId} id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={baseColor} />
          <stop offset="100%" stopColor={darkerColor} />
        </linearGradient>
      );
    });
  };

  // Determine which time period we're showing
  const getTimeframeLabel = () => {
    if (!stats) return "Loading...";

    if (timeFrame === 'today') {
      return "Today's orders";
    } else if (timeFrame === 'thisMonth') {
      // Add safety check for thisMonth and its properties
      if (!stats.thisMonth || !stats.thisMonth.start || !stats.thisMonth.end) {
        return "Monthly orders";
      }
      return `Monthly orders (${stats.thisMonth.start} to ${stats.thisMonth.end})`;
    } else {
      // Add safety check for lastSixMonths and its properties
      if (!stats.lastSixMonths || !stats.lastSixMonths.start || !stats.lastSixMonths.end) {
        return "Six-month orders";
      }
      return `Six-month orders (${stats.lastSixMonths.start} to ${stats.lastSixMonths.end})`;
    }
  };

  // Get all secondary status types from the chart data
  const getSecondaryStatusTypes = () => {
    if (!secondaryChartData.length) return SECONDARY_STATUSES;

    const allTypes = new Set<string>();

    // Add SECONDARY_STATUSES first to ensure they are always included
    SECONDARY_STATUSES.forEach(status => allTypes.add(status));

    // Add other statuses from the data
    secondaryChartData.forEach(dataPoint => {
      Object.keys(dataPoint).forEach(key => {
        if (key !== 'period' && typeof dataPoint[key] === 'number') {
          allTypes.add(key);
        }
      });
    });

    // Return all secondary statuses
    return Array.from(allTypes).filter(status =>
      !PRIMARY_STATUSES.includes(status)
    );
  };

  // Get secondary status types for the chart
  const secondaryStatusTypes = getSecondaryStatusTypes();

  return (
    <ChartCard
      title="Orders Statistics"
      subtitle={!isLoading ? getTimeframeLabel() : "Loading..."}
      className="h-full"
      filters={
        <div className="flex space-x-2">
          <button
            onClick={() => setTimeFrame('today')}
            className={`px-3 py-1 text-xs rounded-md ${timeFrame === 'today'
                ? 'bg-indigo-100 text-indigo-700 font-semibold'
                : 'text-gray-500 hover:bg-gray-100'
              }`}
          >
            Today
          </button>
          <button
            onClick={() => setTimeFrame('thisMonth')}
            className={`px-3 py-1 text-xs rounded-md ${timeFrame === 'thisMonth'
                ? 'bg-indigo-100 text-indigo-700 font-semibold'
                : 'text-gray-500 hover:bg-gray-100'
              }`}
          >
            This Month
          </button>
          <button
            onClick={() => setTimeFrame('lastSixMonths')}
            className={`px-3 py-1 text-xs rounded-md ${timeFrame === 'lastSixMonths'
                ? 'bg-indigo-100 text-indigo-700 font-semibold'
                : 'text-gray-500 hover:bg-gray-100'
              }`}
          >
            Last 6 Months
          </button>
        </div>
      }
    >
      {isLoading ? (
        <div className="h-[500px] w-full flex items-center justify-center">
          <div className="animate-pulse text-gray-400">Loading order statistics...</div>
        </div>
      ) : primaryChartData.length > 0 && secondaryChartData.length > 0 ? (
        <div className="flex flex-col">
          {/* Primary Status Section - Completed and Verification Pending */}
          <div className="mb-2">
            <h3 className="text-md font-semibold text-gray-700 py-2 pl-2 border-l-4 border-indigo-500">Key Metrics</h3>
            <div className="h-60 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={primaryChartData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 10 }}
                  barGap={10}
                  barCategoryGap="30%"
                >
                  <defs>
                    {renderGradients()}
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                  <XAxis
                    dataKey="period"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: '#6B7280' }}
                    dy={10}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: '#6B7280' }}
                    dx={-10}
                  />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(236, 236, 236, 0.2)' }} />
                  <Legend
                    wrapperStyle={{
                      paddingTop: '10px',
                      fontSize: '12px'
                    }}
                    iconType="circle"
                    iconSize={8}
                  />

                  {PRIMARY_STATUSES.map((status) => {
                    const gradientId = `${status.replace(/\s+/g, '')}Gradient`;
                    return (
                      <Bar
                        key={status}
                        dataKey={status}
                        name={status}
                        fill={`url(#${gradientId})`}
                        radius={[6, 6, 0, 0]}
                        animationDuration={1500}
                        animationEasing="ease-out"
                        barSize={60}
                      />
                    );
                  })}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Secondary Status Section - All Other Statuses */}
          <div className="mt-4 pt-2 border-t border-gray-200">
            <h3 className="text-md font-semibold text-gray-700 py-2 pl-2 border-l-4 border-amber-500">Other Statuses</h3>
            <div className="h-60 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={secondaryChartData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 10 }}
                  barSize={20}
                  barGap={2}
                  barCategoryGap="20%"
                >
                  <defs>
                    {renderGradients()}
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                  <XAxis
                    dataKey="period"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: '#6B7280' }}
                    dy={10}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: '#6B7280' }}
                    dx={-10}
                  />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(236, 236, 236, 0.2)' }} />
                  <Legend
                    wrapperStyle={{
                      paddingTop: '10px',
                      fontSize: '12px'
                    }}
                    iconType="circle"
                    iconSize={8}
                  />

                  {secondaryStatusTypes.map((status) => {
                    const gradientId = `${status.replace(/\s+/g, '')}Gradient`;
                    return (
                      <Bar
                        key={status}
                        dataKey={status}
                        name={status}
                        fill={`url(#${gradientId})`}
                        radius={[4, 4, 0, 0]}
                        animationDuration={1500}
                        animationEasing="ease-out"
                      />
                    );
                  })}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      ) : (
        <div className="h-80 w-full flex items-center justify-center">
          <div className="text-gray-500">No order data available.</div>
        </div>
      )}
    </ChartCard>
  );
};

export default OrdersChart;